/**
 * Scout v0 — pre-launch preview + launch (P16, task 3).
 *
 * Renders the FULL composed spec EXACTLY as it will be recorded on the run:
 * spec version, identity, class, always-on suppressions, per-search specifics,
 * every role with its match mode + resolved (equivalent) titles, and the
 * generated boolean string. The raw operator-facing text rendering (the exact
 * artifact recorded with the run) is shown verbatim too.
 *
 * The Launch button lives ONLY on this screen — there is no launch path that
 * skips the preview. The backend also rejects unconfirmed loose roles (409);
 * as defence-in-depth the button is disabled while any loose role still
 * requires operator confirmation, so the UI never offers a bypass.
 */

import type { CriteriaPreview } from '@/types/scout';
import { ErrorBanner } from '@/components/ui';

interface PreviewPanelProps {
  preview: CriteriaPreview;
  /** Launch the previewed spec. Only wired from this screen. */
  onLaunch: () => void;
  /** Go back to the confirm step. */
  onBack: () => void;
  /** Whether the launch request is in flight. */
  launching: boolean;
  /** Launch error message (if the last launch attempt failed). */
  launchError?: string | null;
  /** Retry the failed launch. */
  onRetry?: () => void;
}

function ChipList({ items, empty = '(none)' }: { items: string[]; empty?: string }) {
  if (items.length === 0) return <span className="text-sm text-muted italic">{empty}</span>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <li key={item} className="badge badge-secondary">
          {item}
        </li>
      ))}
    </ul>
  );
}

function sizeLabel(size: CriteriaPreview['specifics']['size']): string {
  if (!size || (size.min === undefined && size.max === undefined)) return '(any)';
  return `${size.min ?? 0}-${size.max ?? '∞'} employees`;
}

export function PreviewPanel({
  preview,
  onLaunch,
  onBack,
  launching,
  launchError,
  onRetry,
}: PreviewPanelProps) {
  const blocked = preview.requiresOperatorConfirmation;

  return (
    <section className="card" aria-labelledby="preview-heading">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id="preview-heading" className="text-lg font-semibold text-text">
            Step 3 - pre-launch preview
          </h2>
          <p className="text-xs text-muted mt-0.5">
            The exact spec recorded on the run. Nothing is sent until you launch.
          </p>
        </div>
        <span className="badge badge-primary font-mono" title="Criteria version stamp">
          {preview.criteriaVersion || '(unversioned)'}
        </span>
      </div>

      <dl className="space-y-4">
        {/* Identity + class */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted mb-1">Identity</dt>
            <dd className="text-sm text-text">{preview.identity || '(not set)'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted mb-1">Class</dt>
            <dd className="text-sm text-text font-mono">{preview.classId || '(none)'}</dd>
          </div>
        </div>

        {/* Suppressions (always-on) */}
        <div>
          <dt className="text-xs font-medium text-muted mb-1">Always-on suppressions</dt>
          <dd className="space-y-2">
            <div>
              <p className="text-xs text-muted mb-1">Clients (never outreach)</p>
              <ChipList items={preview.suppressions.clients} />
            </div>
            <div>
              <p className="text-xs text-muted mb-1">Competitors</p>
              <ChipList items={preview.suppressions.competitors} />
            </div>
            <div>
              <p className="text-xs text-muted mb-1">Bad-fit</p>
              <ChipList items={preview.suppressions.badFit} />
            </div>
          </dd>
        </div>

        {/* Specifics */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium text-muted mb-1">Size band</dt>
            <dd className="text-sm text-text">{sizeLabel(preview.specifics.size)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted mb-1">Geography</dt>
            <dd>
              <ChipList items={preview.specifics.geo} empty="(any)" />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted mb-1">Keywords</dt>
            <dd>
              <ChipList items={preview.specifics.keywords} />
            </dd>
          </div>
        </div>

        {/* Roles + resolved synonyms */}
        <div>
          <dt className="text-xs font-medium text-muted mb-1">Roles &amp; synonyms</dt>
          <dd>
            <ul className="space-y-2">
              {preview.roles.map((role) => (
                <li key={role.title} className="border border-border rounded-md p-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text">{role.title}</span>
                    <span
                      className={`badge ${
                        role.matchMode === 'loose' ? 'badge-secondary' : 'badge-primary'
                      }`}
                    >
                      {role.matchMode}
                    </span>
                    {role.needsConfirmation ? (
                      <span className="badge badge-warning">needs confirmation</span>
                    ) : role.matchMode === 'loose' ? (
                      <span className="badge badge-primary">confirmed</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted mt-1">
                    → {role.resolvedTitles.join(' OR ')}
                  </p>
                </li>
              ))}
            </ul>
          </dd>
        </div>

        {/* Generated boolean */}
        <div>
          <dt className="text-xs font-medium text-muted mb-1">Generated boolean</dt>
          <dd>
            <pre className="text-xs text-text bg-bg border border-border rounded-md p-3 whitespace-pre-wrap break-words font-mono">
              {preview.generatedBoolean || '(empty)'}
            </pre>
          </dd>
        </div>

        {/* Exact recorded text — the artifact that travels with the run. */}
        <div>
          <dt className="text-xs font-medium text-muted mb-1">Recorded spec (verbatim)</dt>
          <dd>
            <pre className="text-xs text-muted bg-bg border border-border rounded-md p-3 whitespace-pre-wrap break-words font-mono">
              {preview.text}
            </pre>
          </dd>
        </div>
      </dl>

      {blocked && (
        <div className="mt-4">
          <ErrorBanner message="This spec still has unconfirmed loose roles. Go back and confirm synonyms before launching." />
        </div>
      )}

      {launchError && (
        <div className="mt-4">
          <ErrorBanner message={launchError} retryable={Boolean(onRetry)} onRetry={onRetry} />
        </div>
      )}

      <div className="flex items-center justify-between mt-5">
        <button type="button" className="btn-ghost" onClick={onBack} disabled={launching}>
          ← Back to confirm
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onLaunch}
          disabled={blocked || launching}
          title={blocked ? 'Confirm all loose roles first' : 'Launch this composed run'}
        >
          {launching ? 'Launching…' : 'Launch run'}
        </button>
      </div>
    </section>
  );
}
